import type { LucideIcon } from "lucide-react";

interface EntitySignalBarProps {
  icon: LucideIcon;
  label: string;
  percentage: number | string | null | undefined;
  accentClassName: string;
  trackClassName: string;
  fillClassName: string;
  helperText?: string;
  className?: string;
}

const normalizePercentage = (
  percentage: number | string | null | undefined,
) => {
  const numericValue = Number(percentage);

  if (!Number.isFinite(numericValue)) {
    return 0;
  }

  return Math.max(0, Math.min(100, numericValue));
};

const formatPercentage = (percentage: number) => {
  const roundedValue = Math.round(percentage * 10) / 10;

  return Number.isInteger(roundedValue)
    ? `${roundedValue}%`
    : `${roundedValue.toFixed(1)}%`;
};

export const EntitySignalBar = ({
  icon: Icon,
  label,
  percentage,
  accentClassName,
  trackClassName,
  fillClassName,
  helperText,
  className = "",
}: EntitySignalBarProps) => {
  const normalizedPercentage = normalizePercentage(percentage);

  return (
    <div
      className={`rounded-lg border border-border/15 bg-muted/20 px-3 py-2 ${className}`}
    >
      <div className="flex items-center gap-2 text-[10px]">
        <div
          className={`flex min-w-0 shrink-0 items-center gap-1 font-semibold ${accentClassName}`}
        >
          <Icon className="h-3 w-3 shrink-0" />
          <span className="truncate">{label}</span>
          {helperText ? (
            <span className="text-muted-foreground">{helperText}</span>
          ) : null}
        </div>
        <span className="w-12 shrink-0 text-right font-bold text-foreground tabular-nums">
          {formatPercentage(normalizedPercentage)}
        </span>
        <div
          className={`h-1.5 min-w-[56px] flex-1 overflow-hidden rounded-full ${trackClassName}`}
        >
          <div
            className={`h-full rounded-full ${fillClassName}`}
            style={{ width: `${normalizedPercentage}%` }}
          />
        </div>
      </div>
    </div>
  );
};
